// Offline storage, talked about only as much as it matters: a quiet ⓘ next to
// the sync status and a one-time tip while storage is unprotected, a banner
// only when unsynced changes have waited too long (lib/storage.ts), and the
// full picture in Settings → This device.
import { Banner, Button, Card, IconButton, Modal, useToast } from '@ims/ui'
import { CheckCircle2, Download, Info, RefreshCw, ShieldAlert } from '@ims/ui/icons'
import { useEffect, useState, useSyncExternalStore } from 'react'
import { Link } from 'react-router'
import { errorMessage, useSyncState } from '../hooks.ts'
import { timeAgo } from '../lib/format.ts'
import { formatBytes, storageAdvice, type StorageAdvice } from '../lib/storage.ts'
import { useSession } from '../session.tsx'
import { useStore, useWorkspace } from '../workspace.tsx'

const TIP_KEY = 'ims.storage-tip-dismissed'

/** Whether the browser can install the app now, and whether it runs installed. */
export function useInstall() {
  const { platform } = useSession()
  const { install } = platform
  const snapshot = useSyncExternalStore(
    install.subscribe,
    () => `${install.available()}|${install.installed()}`,
  )
  const [available, installed] = snapshot.split('|').map((v) => v === 'true')
  return { available: !!available, installed: !!installed, hint: install.manualHint(), install }
}

/** The storage advice, re-evaluated every minute (the at-risk warning is time-based). */
export function useStorageAdvice(): StorageAdvice | null {
  const { storage } = useWorkspace()
  const { oldestUnsyncedAt } = useSyncState()
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(id)
  }, [])
  return storageAdvice(storage, oldestUnsyncedAt, now)
}

/** Install button (when the browser offers it), or how to install by hand. */
function InstallAction() {
  const { available, installed, hint, install } = useInstall()
  const { refreshStorage } = useWorkspace()
  const toast = useToast()
  if (installed) return null
  if (available) {
    return (
      <Button
        size="sm"
        icon={<Download className="size-4" />}
        onClick={() =>
          void install.prompt().then(
            async (outcome) => {
              if (outcome === 'accepted') await refreshStorage()
            },
            (error: unknown) => toast.error(errorMessage(error)),
          )
        }
      >
        Install app
      </Button>
    )
  }
  return hint ? <p className="text-sm text-muted">{hint}</p> : null
}

function StorageExplanation() {
  return (
    <>
      <p>
        This browser can clear offline data if the device runs low on space. Everything that has
        synced is safe on the server; only changes that haven’t synced yet are at risk.
      </p>
      <p>
        Installing the app usually protects its offline data. Syncing regularly keeps the risk small
        either way.
      </p>
    </>
  )
}

/** The ⓘ next to the sync status while offline data isn't protected. */
export function StorageInfoButton() {
  const advice = useStorageAdvice()
  const [open, setOpen] = useState(false)
  if (advice !== 'unprotected' && advice !== 'at-risk') return null
  return (
    <>
      <IconButton
        label="Offline data isn’t protected"
        onClick={() => setOpen(true)}
        className={advice === 'at-risk' ? 'text-danger' : 'text-muted'}
      >
        <Info className="size-4" />
      </IconButton>
      <StorageDialog open={open} onClose={() => setOpen(false)} />
    </>
  )
}

function StorageDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const sync = useSyncState()
  const { engine } = useStore()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  return (
    <Modal open={open} onClose={onClose} title="Keep your offline data safe" size="md">
      <div className="space-y-3 text-sm leading-6">
        <StorageExplanation />
        {sync.pending + sync.failed.length > 0 && (
          <p className="rounded-xl bg-warning-light p-3 text-warning">
            {sync.pending + sync.failed.length} change
            {sync.pending + sync.failed.length === 1 ? ' is' : 's are'} only on this device
            {sync.oldestUnsyncedAt ? ` (oldest ${timeAgo(sync.oldestUnsyncedAt)})` : ''}.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <InstallAction />
          <Button
            size="sm"
            variant="secondary"
            icon={<RefreshCw className="size-4" />}
            loading={busy}
            onClick={() => {
              setBusy(true)
              engine
                .syncNow()
                .catch((error: unknown) => toast.error(errorMessage(error)))
                .finally(() => setBusy(false))
            }}
          >
            Sync now
          </Button>
          <Link
            to="/settings?tab=device"
            onClick={onClose}
            className="text-sm font-medium text-primary hover:underline"
          >
            Device settings
          </Link>
        </div>
      </div>
    </Modal>
  )
}

function readTipDismissed(): boolean {
  try {
    return localStorage.getItem(TIP_KEY) === '1'
  } catch {
    return false
  }
}

/** A one-time tip on the dashboard while storage is unprotected. */
export function StorageTipCard() {
  const advice = useStorageAdvice()
  const { installed } = useInstall()
  const [dismissed, setDismissed] = useState(readTipDismissed)
  if (dismissed || installed || (advice !== 'unprotected' && advice !== 'at-risk')) return null
  const dismiss = () => {
    try {
      localStorage.setItem(TIP_KEY, '1')
    } catch {
      // Private mode: it just shows again next time.
    }
    setDismissed(true)
  }
  return (
    <Card className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-light text-primary">
        <ShieldAlert className="size-5" />
      </span>
      <div className="flex-1 space-y-1 text-sm leading-6">
        <h2 className="font-semibold text-ink">Keep your offline data safe</h2>
        <p className="text-muted">
          Install Sano Godam on this device so the browser keeps its offline data, even when storage
          runs low. Synced data is always safe on the server.
        </p>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <InstallAction />
          <Button size="sm" variant="ghost" onClick={dismiss}>
            Got it
          </Button>
        </div>
      </div>
    </Card>
  )
}

/** Only when it matters: unprotected storage and changes that haven't synced for a while. */
export function StorageBanner() {
  const advice = useStorageAdvice()
  const { engine } = useStore()
  const toast = useToast()
  if (advice !== 'at-risk') return null
  return (
    <Banner
      tone="danger"
      action={
        <Button
          size="sm"
          variant="secondary"
          icon={<RefreshCw className="size-4" />}
          onClick={() => void engine.syncNow().catch((e: unknown) => toast.error(errorMessage(e)))}
        >
          Sync now
        </Button>
      }
    >
      Some changes haven’t synced for over an hour, and this browser may clear offline data when
      storage is low. Connect to the internet and sync.
    </Banner>
  )
}

/** Settings → This device. */
export function DeviceSettings() {
  const { storage } = useWorkspace()
  const sync = useSyncState()
  const { engine } = useStore()
  const { installed } = useInstall()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const unsynced = sync.pending + sync.failed.length
  return (
    <div className="space-y-4">
      <Card className="space-y-4">
        <div className="flex items-center gap-2">
          {storage?.persisted ? (
            <CheckCircle2 className="size-5 text-success" />
          ) : (
            <ShieldAlert className="size-5 text-warning" />
          )}
          <h2 className="font-semibold">
            {storage === null
              ? 'Checking offline storage…'
              : storage.persisted
                ? 'Offline data is protected'
                : 'Offline data isn’t protected'}
          </h2>
        </div>
        {storage && !storage.persisted && (
          <div className="space-y-2 text-sm leading-6 text-muted">
            <StorageExplanation />
          </div>
        )}
        <dl className="grid gap-3 text-sm sm:grid-cols-3">
          <div className="rounded-xl bg-background p-3">
            <dt className="text-muted">App</dt>
            <dd className="font-semibold">{installed ? 'Installed' : 'Running in the browser'}</dd>
          </div>
          <div className="rounded-xl bg-background p-3">
            <dt className="text-muted">Storage used</dt>
            <dd className="font-semibold">
              {formatBytes(storage?.usageBytes)}
              {storage?.quotaBytes ? ` of ${formatBytes(storage.quotaBytes)}` : ''}
            </dd>
          </div>
          <div className="rounded-xl bg-background p-3">
            <dt className="text-muted">Only on this device</dt>
            <dd className="font-semibold">
              {unsynced === 0
                ? 'Nothing: all synced'
                : `${unsynced} change${unsynced === 1 ? '' : 's'}`}
            </dd>
          </div>
        </dl>
        <div className="flex flex-wrap items-center gap-2">
          <InstallAction />
          <Button
            size="sm"
            variant="secondary"
            icon={<RefreshCw className="size-4" />}
            loading={busy}
            onClick={() => {
              setBusy(true)
              engine
                .syncNow()
                .catch((error: unknown) => toast.error(errorMessage(error)))
                .finally(() => setBusy(false))
            }}
          >
            Sync now
          </Button>
          <span className="text-xs text-muted">Last synced {timeAgo(sync.lastPulledAt)}</span>
        </div>
        <p className="text-sm text-muted">
          Changes not yet synced are only on this device. Sync before you uninstall the app, clear
          browser data or sign out.
        </p>
      </Card>
    </div>
  )
}
